// Назначения ролей (contracts/api.md, «Назначения ролей и замещения»).
import { z } from '../kernel/zod.ts';
import { AreasInputSchema, areasFitRole } from '../access/area.ts';
import { AssignmentSchema } from '../access/assignment.ts';
import { fieldMessages } from '../kernel/field-messages.ts';
import { RoleInputSchema } from '../rights/roles.ts';

/** Даты по порядку; ISO-даты сравниваются как строки. */
export function datesInOrder(
  from: string | null | undefined,
  to: string | null | undefined,
): boolean {
  return from == null || to == null || from <= to;
}

/**
 * POST /api/assignments: хотя бы одна область; у client_admin и support — только установка.
 * Срока у собственного назначения нет (FR-014а): validFrom и validTo отклоняются, как любое
 * лишнее поле (REC-007). Срок есть только у замещения — POST /api/substitutions.
 */
export const CreateAssignmentBodySchema = z
  .strictObject({
    userId: z.uuid({ error: fieldMessages.user }),
    role: RoleInputSchema,
    areas: AreasInputSchema,
  })
  .refine((v) => areasFitRole(v.role, v.areas), {
    path: ['areas'],
    error: fieldMessages.areasInstallationOnly,
  });
export type CreateAssignmentBody = z.input<typeof CreateAssignmentBodySchema>;

/**
 * PATCH /api/assignments/{id} с If-Match: роль и области. Срока у собственного назначения нет
 * (FR-014а) — validFrom и validTo отклоняются (REC-007). Смена роли на client_admin или support
 * требует областей «вся установка» — сервер сверяет с текущими областями, если их нет в запросе;
 * смена роли при действующих или запланированных замещениях — REC-006.
 */
export const UpdateAssignmentBodySchema = z
  .strictObject({
    role: RoleInputSchema.optional(),
    areas: AreasInputSchema.optional(),
  })
  .refine((v) => v.role === undefined || v.areas === undefined || areasFitRole(v.role, v.areas), {
    path: ['areas'],
    error: fieldMessages.areasInstallationOnly,
  });
export type UpdateAssignmentBody = z.input<typeof UpdateAssignmentBodySchema>;

export const AssignmentListSchema = z.array(AssignmentSchema);

/**
 * POST /api/assignments/{id}/archive и /restore (T144): If-Match — версия назначения, которую
 * видел пользователь, когда нажал «Снять роль» или «Вернуть». Устаревшая — 409 REC-001 с текущим
 * назначением и последним изменением (FR-028). Без заголовка версия не сверяется: архив и возврат
 * повторяемы — уже снятую роль второй раз не снимают.
 */
export const AssignmentVersionHeadersSchema = z.object({
  'if-match': z.coerce.number().int().min(1).optional(),
});
